// Canvas confetti burst — a dependency-free port of the mockup's celebration
// burst. Call burstConfetti(x, y) with viewport coordinates (e.g. the center
// of a clicked button) and a full-screen canvas is created on demand, animated
// with rAF, and removed when the last particle settles.

const COLORS = ['#ff4d8d', '#ff7a59', '#ffb84d', '#8b5cf6', '#38bdf8', '#2dd4bf', '#a3e635']

const BURST_PARTICLES = 110
const TOUCH_BURST_PARTICLES = 48
const MAX_LIVE_PARTICLES = 240

type Particle = {
  x: number
  y: number
  vx: number
  vy: number
  g: number
  size: number
  rot: number
  vr: number
  color: string
  life: number
  shape: 'rect' | 'circle'
  // Per-particle time-scale: 1 = normal, >1 plays this particle's motion
  // (movement, fall, spin and fade) proportionally faster.
  speed: number
}

let canvas: HTMLCanvasElement | null = null
let ctx: CanvasRenderingContext2D | null = null
let parts: Particle[] = []
let raf: number | null = null

const size = () => {
  if (!canvas) return
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
}

const ensureCanvas = () => {
  if (canvas) return
  canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  canvas.style.cssText =
    'position:fixed;inset:0;pointer-events:none;z-index:9999;'
  document.body.appendChild(canvas)
  ctx = canvas.getContext('2d')
  size()
  window.addEventListener('resize', size)
}

const loop = () => {
  if (!canvas || !ctx) return
  const W = canvas.width
  const H = canvas.height
  ctx.clearRect(0, 0, W, H)
  parts.forEach((p) => {
    const s = p.speed
    p.vy += p.g * s
    p.x += p.vx * s
    p.y += p.vy * s
    p.vx *= 0.99
    p.rot += p.vr * s
    p.life -= 0.008 * s
    ctx!.save()
    ctx!.globalAlpha = Math.max(p.life, 0)
    ctx!.translate(p.x, p.y)
    ctx!.rotate(p.rot)
    ctx!.fillStyle = p.color
    if (p.shape === 'rect') {
      ctx!.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6)
    } else {
      ctx!.beginPath()
      ctx!.arc(0, 0, p.size / 2, 0, Math.PI * 2)
      ctx!.fill()
    }
    ctx!.restore()
  })
  parts = parts.filter((p) => p.life > 0 && p.y < H + 60)
  if (parts.length) {
    raf = requestAnimationFrame(loop)
  } else {
    ctx.clearRect(0, 0, W, H)
    raf = null
    canvas.remove()
    canvas = null
    ctx = null
  }
}

/**
 * Fire a confetti burst at viewport coordinates (defaults to screen center).
 * `speed` time-scales the whole burst — 1 is normal, higher is snappier/faster.
 */
export const burstConfetti = (x?: number, y?: number, speed = 1) => {
  if (typeof window === 'undefined') return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  if (parts.length >= MAX_LIVE_PARTICLES) return
  ensureCanvas()
  const cx = x ?? window.innerWidth / 2
  const cy = y ?? window.innerHeight / 2
  const count = window.matchMedia('(pointer: coarse)').matches ? TOUCH_BURST_PARTICLES : BURST_PARTICLES
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2
    const sp = 4 + Math.random() * 11
    parts.push({
      x: cx,
      y: cy,
      vx: Math.cos(a) * sp,
      vy: Math.sin(a) * sp - 6,
      g: 0.22 + Math.random() * 0.12,
      size: 6 + Math.random() * 8,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.35,
      color: COLORS[(Math.random() * COLORS.length) | 0],
      life: 1,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
      speed,
    })
  }
  if (!raf) loop()
}

/** Convenience click handler: bursts from the clicked element's center.
 * `speed` time-scales the burst (1 = normal, higher = faster). */
export const burstFromEvent = (
  e: { currentTarget: EventTarget & Element },
  speed = 1,
) => {
  const r = e.currentTarget.getBoundingClientRect()
  burstConfetti(r.left + r.width / 2, r.top + r.height / 2, speed)
}
